import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { api } from "../lib/api";

const emptyNew = {
  lgr_id: "",
  cat_sec_seq: 1,
  cat_sec: "",
  cat_sub: "",
  cat_sub_seq: "",
  cat_cod_seq: 1,
  cat_sign: 0,
  cat_amt: "",
  codes_id: "",
};

/* -- shared payload builder for both the Category modal and the ADD accordion -- */
function buildCategoryPayload(form) {
  return {
    lgr_id: form.lgr_id,
    cat_sec_seq: Number(form.cat_sec_seq),
    cat_sec: form.cat_sec || null,
    cat_sub: form.cat_sub || null,
    cat_sub_seq: form.cat_sub_seq === "" || form.cat_sub_seq == null
                  ? null : Number(form.cat_sub_seq),
    cat_cod_seq: Number(form.cat_cod_seq),
    cat_sign: Number(form.cat_sign),
    cat_amt: parseFloat(form.cat_amt || 0) || 0,
    codes_id: form.codes_id,
  };
}

/* -- Inner form body for the Category modal -- */
function CategoryForm({ form, set, ledgers, codes }) {
  return (
    <div className="form-grid">
      {/* Row 1: Ledger across full width */}
      <div className="form-field" style={{ gridColumn: "span 3" }}>
        <label>Ledger <span className="req">*</span></label>
        <select
          data-testid="catm-lgr"
          value={form.lgr_id}
          onChange={(e) => set("lgr_id", e.target.value)}
          required
        >
          {ledgers?.map((l) => (
            <option key={l.lgr_id} value={l.lgr_id}>{l.lgr_lit}</option>
          ))}
        </select>
      </div>

      {/* Row 2: Section + Section Seq */}
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Section</label>
        <input data-testid="catm-sec" value={form.cat_sec} maxLength={32}
               onChange={(e) => set("cat_sec", e.target.value)} />
      </div>
      <div className="form-field">
        <label>Section Seq</label>
        <input data-testid="catm-sec-seq" type="number" value={form.cat_sec_seq}
               onChange={(e) => set("cat_sec_seq", e.target.value)} required />
      </div>

      {/* Row 3: Category + Category Seq */}
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Category</label>
        <input data-testid="catm-sub" value={form.cat_sub} maxLength={32}
               onChange={(e) => set("cat_sub", e.target.value)} />
      </div>
      <div className="form-field">
        <label>Category Seq</label>
        <input data-testid="catm-sub-seq" type="number"
               value={form.cat_sub_seq ?? ""}
               onChange={(e) => set("cat_sub_seq", e.target.value)} />
      </div>

      {/* Row 4: Code + Code Seq */}
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Code <span className="req">*</span></label>
        <select
          data-testid="catm-code"
          value={form.codes_id}
          onChange={(e) => set("codes_id", e.target.value)}
          required
        >
          <option value="">— pick code —</option>
          {codes?.map((c) => (
            <option key={c.id} value={c.id}>{c.id} · {c.label}</option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label>Code Seq</label>
        <input data-testid="catm-cod-seq" type="number" value={form.cat_cod_seq}
               onChange={(e) => set("cat_cod_seq", e.target.value)} required />
      </div>

      {/* Row 5: Budget Amount + Sign */}
      <div className="form-field" style={{ gridColumn: "span 2" }}>
        <label>Budget Amount</label>
        <input data-testid="catm-amt" type="number" step="0.01"
               value={form.cat_amt}
               onChange={(e) => set("cat_amt", e.target.value)} />
      </div>
      <div className="form-field">
        <label>Sign</label>
        <select data-testid="catm-sign" value={form.cat_sign}
                onChange={(e) => set("cat_sign", e.target.value)}>
          <option value={0}>0 (positive)</option>
          <option value={1}>1 (negative)</option>
        </select>
      </div>
    </div>
  );
}

function CategoryModal({ mode, category, ledgers, codes, canWrite = true, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState(
    isNew
      ? { ...emptyNew, lgr_id: ledgers?.[0]?.lgr_id || "" }
      : { ...category, cat_amt: String(category.cat_amt ?? "") }
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-reset the delete-confirm flag after 5s so users don't accidentally
  // delete next time they open the modal flow.
  useEffect(() => {
    if (!confirmDel) return;
    const t = setTimeout(() => setConfirmDel(false), 5000);
    return () => clearTimeout(t);
  }, [confirmDel]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = buildCategoryPayload(form);
      if (isNew) {
        await api.createCategory(payload);
      } else {
        await api.updateCategory(category.cat_seq, payload);
      }
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    // First click arms the action; second click commits the delete.
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy(true);
    try { await api.deleteCategory(category.cat_seq); onSaved(); }
    catch (e2) { setErr(e2.response?.data?.detail || e2.message); setConfirmDel(false); }
    finally { setBusy(false); }
  };

  let submitLabel = "Save changes";
  if (busy) submitLabel = "Saving…";
  else if (isNew) submitLabel = "Create category";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1080 }} data-testid="cat-modal">
        <div className="modal-head">
          <h2>{isNew ? "New Category" : `Edit category · seq ${category.cat_seq}`}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="catm-close">×</button>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <CategoryForm form={form} set={set} ledgers={ledgers} codes={codes} />
          {err && <div className="form-error" data-testid="catm-error">{err}</div>}
          <div className="modal-actions no-divider" style={{ justifyContent: "space-between" }}>
            <div>
              {!isNew && canWrite && (
                <button
                  type="button"
                  className={`btn-danger ${confirmDel ? "armed" : ""}`}
                  data-testid="catm-delete"
                  onClick={onDelete}
                  disabled={busy}
                  title={confirmDel ? "Click again to confirm permanent delete" : "Delete this category"}
                >
                  {confirmDel ? "Click again to confirm delete" : "Delete category"}
                </button>
              )}
            </div>
            {canWrite && (
              <button type="submit" className="btn-primary" data-testid="catm-submit"
                      disabled={busy}
                      style={{ width: "auto", padding: "10px 22px", margin: 0 }}>
                {submitLabel}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/* -- Compact amount cell: shows "(123.45)" with red tint when negative -- */
function CategoryAmountCell({ catSign, catAmt }) {
  const isNeg = catSign === 1;
  const formatted = `$${Number(catAmt).toFixed(2)}`;
  return (
    <td className={isNeg ? "neg" : ""} style={{ textAlign: "right" }}>
      {isNeg ? `(${formatted})` : formatted}
    </td>
  );
}

/* -- Single row in the categories table -- */
function CategoryRow({ r, onEdit }) {
  return (
    <tr data-testid={`cat-row-${r.cat_seq}`}>
      <td onClick={onEdit} style={{ cursor: "pointer" }}>{r.cat_sec || "—"}</td>
      <td onClick={onEdit} style={{ cursor: "pointer" }}>{r.cat_sub || "—"}</td>
      <td onClick={onEdit} style={{ cursor: "pointer", textAlign: "right" }}>{r.cat_sub_seq ?? "—"}</td>
      <td onClick={onEdit} style={{ cursor: "pointer" }}><b>{r.codes_id}</b></td>
      <td onClick={onEdit} style={{ cursor: "pointer" }}>{r.codes_lit}</td>
      <td onClick={onEdit} style={{ cursor: "pointer", textAlign: "right" }}>{r.cat_cod_seq}</td>
      <td onClick={onEdit} style={{ cursor: "pointer", textAlign: "right" }}>{r.cat_sign}</td>
      <CategoryAmountCell catSign={r.cat_sign} catAmt={r.cat_amt} />
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <button
          type="button"
          className="row-action-icon"
          data-testid={`cat-edit-${r.cat_seq}`}
          onClick={onEdit}
          title="Edit category"
          aria-label="Edit category"
        >
          <Pencil size={15} strokeWidth={2} />
        </button>
      </td>
    </tr>
  );
}

/* -- Table body: handles loading / empty / data states -- */
function CategoriesTable({ rows, onRowEdit }) {
  if (!rows) {
    return <div className="loading-row"><span className="spinner" /> Loading categories…</div>;
  }
  if (rows.length === 0) {
    return <div className="empty">No category rows for this ledger.</div>;
  }
  return (
    <table className="txn-table" style={{ fontSize: 13 }}>
      <thead>
        <tr>
          <th>Section</th>
          <th>Sub</th>
          <th style={{ width: 60, textAlign: "right" }}>Sub Seq</th>
          <th>Code</th>
          <th>Description</th>
          <th style={{ width: 60, textAlign: "right" }}>Cod Seq</th>
          <th style={{ width: 60, textAlign: "right" }}>Sign</th>
          <th style={{ width: 130, textAlign: "right" }}>Amount</th>
          <th style={{ width: 60, textAlign: "right" }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <CategoryRow
            key={r.cat_seq}
            r={r}
            onEdit={() => onRowEdit(r)}
          />
        ))}
      </tbody>
    </table>
  );
}

/* -- Modal: edit a single ledger's metadata + its category rows -- */
function LedgerEditModal({ mode, ledger, allLedgers, codes, canWrite = true, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState(
    isNew
      ? { lgr_id: "", lgr_lit: "" }
      : { lgr_id: ledger.lgr_id, lgr_lit: ledger.lgr_lit || "" }
  );
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [catModal, setCatModal] = useState(null);        // for EDIT existing
  const [confirmDel, setConfirmDel] = useState(false);   // 2-click delete state
  const [assignOpen, setAssignOpen] = useState(false);   // new combined ADD+COPY modal

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCategories = async (lgr_id) => {
    if (!lgr_id) return;
    try { setRows(await api.listCategories(lgr_id)); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => {
    if (isNew) { setRows([]); return; }
    loadCategories(form.lgr_id);
  }, [isNew, form.lgr_id]);

  const saveLedger = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isNew) {
        await api.createLedger({
          lgr_id: form.lgr_id.trim(),
          lgr_lit: form.lgr_lit.trim(),
        });
      } else {
        const patch = {};
        if (form.lgr_lit !== (ledger.lgr_lit || "")) patch.lgr_lit = form.lgr_lit.trim();
        if (Object.keys(patch).length) await api.updateLedger(ledger.lgr_id, patch);
      }
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  // Guard: refuse locally while categories still exist (backend also
  // enforces this via a 409). Returns a block reason, or null if OK to arm/delete.
  const ledgerDeleteBlockReason = (() => {
    if (rows === null) return "Loading categories…";
    if (rows.length > 0) return "Ledger cannot be deleted if it has associated categories";
    return null;
  })();

  const onDeleteLedger = async () => {
    if (ledgerDeleteBlockReason) {
      setErr(ledgerDeleteBlockReason);
      setConfirmDel(false);
      return;
    }
    // Two-click confirmation: first click arms the button, second click deletes.
    if (!confirmDel) {
      setErr("");
      setConfirmDel(true);
      return;
    }
    setErr(""); setBusy(true);
    try { await api.deleteLedger(ledger.lgr_id); onSaved(); }
    catch (e2) {
      const status = e2.response?.status;
      setErr(status === 409
        ? "Ledger cannot be deleted if it has associated categories"
        : e2.response?.data?.detail || e2.message);
      setConfirmDel(false);
    }
    finally { setBusy(false); }
  };

  let submitLabel = "Save";
  if (busy) submitLabel = "Saving…";
  else if (isNew) submitLabel = "Create ledger";

  const hasCats = rows && rows.length > 0;
  const deleteLabel = confirmDel ? "Click again to confirm" : "Delete ledger";
  let deleteTitle = "Delete this ledger";
  if (hasCats) deleteTitle = "This ledger has categories; remove them first";
  else if (confirmDel) deleteTitle = "Click again to confirm";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1320 }}
        data-testid="ledger-modal"
      >
        <div className="modal-head">
          <h2>{isNew ? "New Ledger" : `Edit ledger · ${ledger.lgr_id}`}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="lm-close">×</button>
        </div>
        <form className="modal-form" onSubmit={saveLedger}>
          <div className="form-grid">
            <div className="form-field">
              <label>Ledger ID <span className="req">*</span></label>
              <input
                data-testid="lm-id"
                value={form.lgr_id}
                maxLength={32}
                disabled={!isNew}
                onChange={(e) => set("lgr_id", e.target.value)}
                required={isNew}
              />
            </div>
            <div className="form-field" style={{ gridColumn: "span 3" }}>
              <label>Ledger Description <span className="req">*</span></label>
              <input
                data-testid="lm-lit"
                value={form.lgr_lit}
                maxLength={50}
                onChange={(e) => set("lgr_lit", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Save (right) / Delete Ledger (left) */}
          <div className="modal-actions no-divider"
               style={{ justifyContent: "space-between", alignItems: "center",
                        marginTop: 4, marginBottom: 6 }}>
            <div style={{ display: "flex", gap: 10 }}>
              {!isNew && canWrite && (
                <button
                  type="button"
                  className="btn-danger"
                  data-testid="lm-delete"
                  onClick={onDeleteLedger}
                  disabled={busy}
                  title={deleteTitle}
                >{deleteLabel}</button>
              )}
            </div>
            {canWrite && (
              <button
                type="submit"
                className="btn-primary"
                data-testid="lm-submit"
                disabled={busy}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}
              >{submitLabel}</button>
            )}
          </div>

          {!isNew && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                            borderTop: "1px solid var(--nyse-line)", paddingTop: 16, marginBottom: 10 }}>
                <p className="section-title" style={{ margin: 0 }}>Categories assigned</p>
                {canWrite && (
                  <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                    <button
                      type="button"
                      className="btn-navy"
                      data-testid="lm-assign-cat"
                      onClick={() => { setErr(""); setInfo(""); setConfirmDel(false); setAssignOpen(true); }}
                      disabled={busy}
                      style={{ width: "auto", padding: "8px 20px", margin: 0,
                               letterSpacing: ".12em" }}
                    >+ Assign Category</button>
                  </div>
                )}
              </div>
              <CategoriesTable
                rows={rows}
                onRowEdit={(r) => setCatModal({ mode: "edit", category: r })}
              />
            </div>
          )}

          {err && <div className="form-error" data-testid="lm-error">{err}</div>}
          {info && (
            <div
              data-testid="lm-info"
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid #b6d7a8",
                background: "#e7f4dd",
                color: "#1f5b1f",
                fontSize: 13,
              }}
            >{info}</div>
          )}
        </form>
      </div>

      {catModal && (
        <CategoryModal
          mode={catModal.mode}
          category={catModal.mode === "new"
            ? undefined
            : catModal.category}
          ledgers={allLedgers}
          codes={codes}
          canWrite={canWrite}
          onClose={() => setCatModal(null)}
          onSaved={() => {
            setCatModal(null);
            loadCategories(form.lgr_id);
          }}
        />
      )}

      {assignOpen && (
        <AssignCategoryModal
          targetLgrId={ledger.lgr_id}
          allLedgers={allLedgers}
          codes={codes}
          onClose={() => setAssignOpen(false)}
          onSaved={(msg) => {
            setAssignOpen(false);
            loadCategories(form.lgr_id);
            setErr("");
            if (msg) setInfo(msg);
          }}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------
 * AssignCategoryModal — combined ADD + COPY accordion. Replaces the two
 * previously-separate flows. Exactly one section is open at a time, with
 * ADD open by default.
 * -------------------------------------------------------------------- */

function AccordionHeader({ id, open, label, onToggle, count }) {
  return (
    <button
      type="button"
      data-testid={`acm-acc-${id}`}
      onClick={onToggle}
      className="acm-acc-head"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        background: open ? "#8a8a8a" : "#c4c4c4",   // medium grey: darker when open, lighter when closed
        color: open ? "#fff" : "#1a1a1a",
        border: "1px solid var(--nyse-line)",
        borderBottom: open ? "none" : "1px solid var(--nyse-line)",
        cursor: "pointer",
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        fontSize: 12,
      }}
    >
      <span>{label} {count != null && <span style={{ opacity: .7 }}>({count})</span>}</span>
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>{open ? "▾" : "▸"}</span>
    </button>
  );
}

/** ADD-mode body: a fresh category form bound to the current ledger. */
function AddCategoryPanel({ targetLgrId, allLedgers, codes, onSaved }) {
  const [form, setForm] = useState({
    ...emptyNew,
    lgr_id: targetLgrId,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.createCategory(buildCategoryPayload(form));
      onSaved("Category added.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <form className="acm-acc-body modal-form" onSubmit={submit}
          style={{ border: "1px solid var(--nyse-line)", borderTop: "none",
                   padding: 16, marginBottom: 8 }}>
      <CategoryForm form={form} set={set} ledgers={allLedgers} codes={codes} />
      {err && <div className="form-error" data-testid="acm-add-error">{err}</div>}
      <div className="modal-actions no-divider"
           style={{ justifyContent: "flex-end", marginTop: 8 }}>
        <button type="submit" className="btn-primary"
                data-testid="acm-add-submit"
                disabled={busy}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}>
          {busy ? "Saving…" : "Create category"}
        </button>
      </div>
    </form>
  );
}

/* -- pure helpers for the source-ledger category filters (kept outside the
   component so they don't contribute to its cyclomatic complexity) -- */
function buildSecOptions(catRows) {
  const seen = new Map();
  for (const r of catRows) {
    if (!seen.has(r.cat_sec_seq)) seen.set(r.cat_sec_seq, r.cat_sec || "");
  }
  return [...seen.entries()]
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([secSeq, label]) => ({ sec: secSeq, label: `${secSeq}${label ? " · " + label : ""}` }));
}

function buildSubOptions(catRows, catSec) {
  const filtered = catSec === ""
    ? catRows
    : catRows.filter((r) => String(r.cat_sec_seq) === String(catSec));
  const seen = new Set();
  for (const r of filtered) {
    if (r.cat_sub != null && r.cat_sub !== "") seen.add(r.cat_sub);
  }
  return [...seen].sort();
}

function countMatches(catRows, catSec, catSub) {
  return catRows.filter((r) =>
    (catSec === "" || String(r.cat_sec_seq) === String(catSec)) &&
    (catSub === "" || r.cat_sub === catSub)
  ).length;
}

/** COPY-mode body: source ledger + cat_sec + cat_sub filters. */
function CopyCategoriesPanel({ targetLgrId, allLedgers, onSaved }) {
  const sourceOptions = (allLedgers || []).filter((l) => l.lgr_id !== targetLgrId);
  const [srcLgr, setSrcLgr] = useState(sourceOptions[0]?.lgr_id || "");
  const [srcCats, setSrcCats] = useState(null);
  const [catSec, setCatSec] = useState("");
  const [catSub, setCatSub] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setErr("");
    setCatSec("");
    setCatSub("");
    if (!srcLgr) { setSrcCats([]); return; }
    setSrcCats(null);
    api.listCategories(srcLgr)
      .then((rows) => { if (!cancelled) setSrcCats(rows || []); })
      .catch((e) => { if (!cancelled) { setSrcCats([]); setErr(e.response?.data?.detail || e.message); } });
    return () => { cancelled = true; };
  }, [srcLgr]);

  const catRows = srcCats || [];
  const secOptions = buildSecOptions(catRows);
  const subOptions = buildSubOptions(catRows, catSec);
  useEffect(() => {
    if (catSub && !subOptions.includes(catSub)) setCatSub("");
  }, [catSec, subOptions, catSub]);

  const matchedCount = countMatches(catRows, catSec, catSub);

  const onConfirm = async () => {
    setErr("");
    if (!srcLgr) { setErr("Pick a source ledger"); return; }
    setBusy(true);
    try {
      const res = await api.copyCategoriesFiltered({
        from_lgr_id: srcLgr,
        to_lgr_id: targetLgrId,
        cat_sec_seq: catSec === "" ? null : Number(catSec),
        cat_sub: catSub === "" ? null : catSub,
      });
      const n = res.inserted || 0;
      onSaved(n > 0
        ? `Copied ${n} categor${n === 1 ? "y" : "ies"} from source ledger.`
        : "No matching categories were found to copy.");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="acm-acc-body modal-form"
         style={{ border: "1px solid var(--nyse-line)", borderTop: "none",
                  padding: 16, marginBottom: 8 }}>
      <p className="muted" style={{ marginTop: 0 }}>
        Pick a source ledger, then narrow with Section and/or Sub-Category.
        Matching categories are appended to <b>{targetLgrId}</b> with cat_amt
        reset to $0.
      </p>
      <div className="form-grid">
        <div className="form-field" style={{ gridColumn: "span 3" }}>
          <label>Source Ledger <span className="req">*</span></label>
          <select
            data-testid="acm-copy-src"
            value={srcLgr}
            onChange={(e) => setSrcLgr(e.target.value)}
            required
          >
            {sourceOptions.map((l) => (
              <option key={l.lgr_id} value={l.lgr_id}>
                {l.lgr_id} · {l.lgr_lit}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ gridColumn: "span 3" }}>
          <label>Section</label>
          <select
            data-testid="acm-copy-sec"
            value={catSec}
            onChange={(e) => setCatSec(e.target.value)}
            disabled={!secOptions.length}
          >
            <option value="">— all sections —</option>
            {secOptions.map((o) => (
              <option key={o.sec} value={o.sec}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ gridColumn: "span 3" }}>
          <label>Sub-Category</label>
          <select
            data-testid="acm-copy-sub"
            value={catSub}
            onChange={(e) => setCatSub(e.target.value)}
            disabled={!subOptions.length}
          >
            <option value="">— all sub-categories —</option>
            {subOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="muted" data-testid="acm-copy-count" style={{ marginTop: 14 }}>
        {(() => {
          if (!srcLgr) return "Pick a source ledger above.";
          if (srcCats === null) return "Loading source categories…";
          if (srcCats.length === 0) return "No categories on the source ledger.";
          return `${matchedCount} categor${matchedCount === 1 ? "y" : "ies"} match the current filters.`;
        })()}
      </p>
      {err && <div className="form-error" data-testid="acm-copy-error">{err}</div>}
      <div className="modal-actions no-divider"
           style={{ justifyContent: "flex-end", gap: 10 }}>
        <button type="button" className="btn-primary"
                data-testid="acm-copy-confirm"
                onClick={onConfirm}
                disabled={busy || !srcLgr || matchedCount === 0}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}>
          {busy ? "Copying…" : `Copy ${matchedCount || ""} categor${matchedCount === 1 ? "y" : "ies"}`}
        </button>
      </div>
    </div>
  );
}

function AssignCategoryModal({ targetLgrId, allLedgers, codes, onClose, onSaved }) {
  const [open, setOpen] = useState("add");   // "add" | "copy" — exactly one open
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card"
           onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 1080 }}
           data-testid="assign-cat-modal">
        <div className="modal-head">
          <h2>Assign category · {targetLgrId}</h2>
          <button className="modal-close" onClick={onClose}
                  aria-label="Close" data-testid="acm-close">×</button>
        </div>
        <div className="modal-form" style={{ paddingBottom: 8 }}>
          <AccordionHeader
            id="add"
            label="Add"
            open={open === "add"}
            onToggle={() => setOpen("add")}
          />
          {open === "add" && (
            <AddCategoryPanel
              targetLgrId={targetLgrId}
              allLedgers={allLedgers}
              codes={codes}
              onSaved={onSaved}
            />
          )}
          <AccordionHeader
            id="copy"
            label="Copy"
            open={open === "copy"}
            onToggle={() => setOpen("copy")}
          />
          {open === "copy" && (
            <CopyCategoriesPanel
              targetLgrId={targetLgrId}
              allLedgers={allLedgers}
              onSaved={onSaved}
            />
          )}
        </div>
      </div>
    </div>
  );
}


/* -- Row in the all-ledgers list table -- */
function LedgerRow({ l, onClick }) {
  return (
    <tr
      data-testid={`ledger-row-${l.lgr_id}`}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <td><b>{l.lgr_id}</b></td>
      <td>{l.lgr_lit}</td>
    </tr>
  );
}

export default function AdminCategories({ open = false, onToggle, standalone = false, canWrite = true }) {
  const [ledgers, setLedgers] = useState(null);
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);
  const isOpen = standalone || open;

  const reloadLedgers = async () => {
    try {
      const lgs = await api.ledgers();
      setLedgers(lgs);
      return lgs;
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([api.ledgers(), api.refs()])
      .then(([lgs, refs]) => { setLedgers(lgs); setCodes(refs.codes); })
      .catch((e) => setErr(e.message));
  }, [isOpen]);

  return (
    <>
      <section className={`admin-section ${isOpen ? "open" : "closed"} ${standalone ? "standalone" : ""}`} data-testid="admin-section-categories">
        <div className="admin-section-body">
          <div className="card list-card" data-testid="admin-cats-card">
            {/* Top bar: page title LEFT + "+ New Ledger" right-justified */}
            <div className="list-header">
              <div className="list-header-left">
                <p className="section-title" style={{ margin: 0 }}>Ledgers</p>
              </div>
              <div className="list-header-right">
                {canWrite && (
                  <button
                    type="button"
                    className="btn-primary"
                    data-testid="new-ledger-btn"
                    onClick={() => setModal({ mode: "new" })}
                  >+ New Ledger</button>
                )}
              </div>
            </div>

            {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}

            {/* List of all ledgers */}
            {(() => {
              if (!ledgers) {
                return <div className="loading-row"><span className="spinner" /> Loading ledgers…</div>;
              }
              if (ledgers.length === 0) {
                return <div className="list-empty empty">No ledgers defined. Click &quot;+ New Ledger&quot; to get started.</div>;
              }
              return (
                <div className="list-scroll">
                <table className="list-table list-table-compact txn-table" data-testid="ledgers-table">
                  <thead>
                    <tr className="list-head-labels">
                      <th>lgr_id</th>
                      <th>lgr_lit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgers.map((l) => (
                      <LedgerRow
                        key={l.lgr_id}
                        l={l}
                        onClick={() => setModal({ mode: "edit", ledger: l })}
                      />
                    ))}
                  </tbody>
                </table>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {modal && (
        <LedgerEditModal
          mode={modal.mode}
          ledger={modal.ledger}
          allLedgers={ledgers}
          codes={codes}
          canWrite={canWrite}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reloadLedgers(); }}
        />
      )}
    </>
  );
}
